import React, { useMemo } from 'react'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { makeStyles } from '@material-ui/core/styles'
import { Tooltip, IconButton } from '@material-ui/core'
import { Archive, Unarchive } from '@material-ui/icons'
import { useMutation } from '@apollo/client'
import _ from 'lodash'
import { Avatar } from '../components/Avatar'
import {
  CHAT_ROOM_TYPE,
  MESSAGE_TABS,
  MESSAGE_TYPE,
  capitalizeFirstLetter,
} from '../../../lib/constants'
import { ImportantDialog } from '../components/ImportantDialog'
import ArchiveChatRoom from '../graphql/mutations/archiveChatRoom.gql'
import UnarchiveChatRoom from '../graphql/mutations/unarchiveChatRoom.gql'

dayjs.extend(relativeTime)
const useStyles = makeStyles({
  unread: { fontWeight: 'bold' },
  read: { fontWeight: 'regular' },
  wrap: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  threadContainer: {
    marginLeft: '10px',
    paddingLeft: '5px',
    paddingBottom: '20px',
    borderBottom: '1px solid #E0E0E0',
  },

  threadLabel: {
    fontSize: '16px',
  },

  badge: {
    backgroundColor: '#00A7A4',
  },
  archive: {
    position: 'absolute',
    bottom: '-20px',
    right: '8px',
  },
  relative: {
    position: 'relative',
  },
  tooltip: {
    marginTop: 4,
  },
})

export const ThreadMessage = ({
  userId,
  setThread,
  setPanel,
  room,
  archived,
}) => {
  const classes = useStyles()
  const [open, setOpen] = React.useState(false)
  const message = useMemo(
    () => (room.chatMessages.items.length > 0 ? room.chatMessages.items[0] : []),
    [room.chatMessages.items],
  )
  const participants = room.chatParticipants
  const sentAt = message.createdAt
  const isReceived = room.chatMessages.items.length > 0
    && room.chatMessages.items[0].user.id !== userId
  const unread = isReceived && !message.isViewed

  const [archiveRoom, { loading: archiving }] = useMutation(ArchiveChatRoom, {
    onCompleted: () => setOpen(false),
    refetchQueries: ['ChatRooms'],
  })

  const [unarchiveRoom, { loading: unArchiving }] = useMutation(UnarchiveChatRoom, {
    onCompleted: () => setOpen(false),
    refetchQueries: ['ChatRooms'],
  })
  const loading = archiving || unArchiving

  const handler = () => {
    setThread({
      type: room.roomType,
      roomId: room.id,
      users: participants.map((user) => user.user),
      archived,
    })
    setPanel(MESSAGE_TABS.THREAD)
  }

  const handleAction = (e) => {
    e.stopPropagation()
    setOpen(true)
  }

  const confirmAction = () => {
    if (archived) {
      unarchiveRoom({ variables: { chatRoomId: room.id } })
    } else {
      archiveRoom({ variables: { chatRoomId: room.id } })
    }
  }

  const name = useMemo(() => {
    if (room.roomType === CHAT_ROOM_TYPE.GROUP) {
      return room.name
    }
    const num = participants.length
    const listName = participants.map((user) => user.user.fullName)
    if (num <= 2) {
      return listName.join(', ')
    }
    return `${listName.slice(0, 2).join(', ')} +${num - 2}`
  }, [participants, room])

  const content = useMemo(() => {
    if (archived) {
      return `Archived by ${_.get(room, 'archivedBy.fullName')} at ${dayjs(
        room.archivedAt,
      ).format('h:mm A')} on ${dayjs(room.archivedAt).format('DD/MM/YYYY')}`
    }
    return message.messageType === MESSAGE_TYPE.SYSTEM
      ? capitalizeFirstLetter(message.content)
      : message.content
  }, [archived, room, message])

  const classNames = [
    'col-sx-10 col-sm-10 col-md-10',
    classes.wrap,
    unread ? classes.unread : classes.read,
  ].join(' ')

  return (
    <>
      <div
        className={` ${classes.threadContainer} row`}
        tabIndex={0}
        role="link"
        onKeyDown={() => {}}
        onClick={handler}
      >
        <div className="col-sx-12 col-sm-12 col-md-12">
          <div className="row">
            <div className="col-sx-2 col-sm-2 col-md-2 margin-top-10">
              <Avatar
                thumbnail={
                  participants.length > 0 && participants[0].user.avatar.thumb
                }
                name={room.name}
              />
            </div>
            <div className="col-sx-10 col-sm-10 col-md-10">
              <div className="row">
                <div
                  className={`col-sx-6 col-sm-6 col-md-6 padding-vert-10 ${classes.threadLabel}`}
                >
                  {name}
                </div>
                <div className="col-sx-6 col-sm-6 col-md-6 padding-vert-10 text-right">
                  {dayjs(sentAt).fromNow()}
                </div>
              </div>
              <div className={`row ${classes.relative}`}>
                <div className={classNames}>
                  <Tooltip
                    title={content}
                    placement="bottom-start"
                    classes={{ tooltip: classes.tooltip }}
                  >
                    <span>{content}</span>
                  </Tooltip>
                </div>
                <div className="col-sx-2 col-sm-2 col-md-2">
                  {room.totalUnread > 0 && (
                    <span className={`badge ${classes.badge}`}>
                      {room.totalUnread}
                    </span>
                  )}
                </div>
              </div>
              <div className={classes.archive}>
                <Tooltip
                  title={archived ? 'Unarchive' : 'Archive'}
                  classes={{ tooltip: classes.tooltip }}
                >
                  <IconButton size="small" onClick={handleAction}>
                    {archived ? (
                      <Unarchive color="primary" fontSize="small" />
                    ) : (
                      <Archive color="primary" fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ImportantDialog
        open={open}
        onCancel={() => setOpen(false)}
        onConfirm={confirmAction}
        title={archived ? 'Unarchive Chat' : 'Archive Chat'}
        confirmText={archived ? 'Unarchive' : 'Archive'}
        loading={loading}
        content={`Are you sure you want to ${
          archived ? 'unarchive' : 'archive'
        } this chat?`}
      />
    </>
  )
}
